import { useCallback, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowDown, ArrowUp, Plus, Send, X } from "lucide-react";
import pageStyles from "../../styles/PageLayout.module.css";
import IntegrationsPanel from "../../components/settings/IntegrationsPanel";
import ChatPage from "../ChatPage";
import styles from "./IntegrationsPage.module.css";
import { useAuthUser } from "../../contexts/AuthUserContext";
import { API_BASE } from "../../utils/api/api";

const DRAG_START_MOUSE_KEY = "__zeqiIntegrationsPageStartMouseDrag";
const DRAG_START_TOUCH_KEY = "__zeqiIntegrationsPageStartTouchDrag";

const IntegrationsPage = () => {
  const splitRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, signIn } = useAuthUser();
  const isOnboarding = location.pathname === "/onboarding/integrations";
  const [skipping, setSkipping] = useState(false);

  const handleSkip = async () => {
    setSkipping(true);
    try {
      const res = await fetch(`${API_BASE}/api/users/onboarding/integrations/complete`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${user?.token}`,
        },
        body: JSON.stringify({ provider: "skip" }),
      });
      const data = await res.json();
      if (data.onboarding) {
        signIn({ onboarding: data.onboarding });
      } else {
        signIn({
          onboarding: {
            ...(user?.onboarding || {}),
            currentStep: "home",
          },
        });
      }
    } catch {
      signIn({
        onboarding: { ...(user?.onboarding || {}), currentStep: "home" },
      });
    } finally {
      setSkipping(false);
      navigate("/", { replace: true });
    }
  };
  const [topFlex, setTopFlex] = useState(0.42);
  const [chatPaneState, setChatPaneState] = useState("default");
  const [dismissedContextKeys, setDismissedContextKeys] = useState([]);
  const isChatClosed = chatPaneState === "minimized";
  const handleExpandChat = useCallback(() => {
    setChatPaneState((current) => {
      if (current === "minimized") return "default";
      if (current === "default") return "maximized";
      return "default";
    });
  }, []);
  const handleExpandReports = useCallback(() => {
    setChatPaneState((current) => {
      if (current === "maximized") return "default";
      if (current === "default") return "minimized";
      return "minimized";
    });
    setTopFlex((current) => Math.max(current, 0.72));
  }, []);

  const baseChatContextTrail = useMemo(
    () => [
      {
        label: "Integrations",
        type: "channel",
        key: "integrations",
      },
    ],
    [],
  );

  const visibleChatContextTrail = useMemo(() => {
    const next = baseChatContextTrail.filter(
      (item) => !dismissedContextKeys.includes(item.key),
    );

    if (next.length) {
      return next;
    }

    return [
      {
        label: "Main",
        type: "main",
        key: "main",
      },
    ];
  }, [baseChatContextTrail, dismissedContextKeys]);

  const activeChatContext = visibleChatContextTrail[visibleChatContextTrail.length - 1] || {
    label: "Main",
    type: "main",
    key: "main",
  };

  const resetSpecificContext = useCallback((contextKey, contextLabel) => {
    setDismissedContextKeys((current) =>
      current.includes(contextKey) ? current : [...current, contextKey],
    );
    if (contextLabel) {
      window.dispatchEvent(
        new CustomEvent("zeqi:chat-context-notification", {
          detail: { type: "removed", label: contextLabel, key: contextKey },
        }),
      );
    }
  }, []);

  const startResize = useCallback((startY) => {
    const shell = splitRef.current;
    if (!shell) return;

    const shellRect = shell.getBoundingClientRect();
    const shellHeight = shellRect.height || 1;
    const initialTopHeight = shellHeight * topFlex;

    const updateFromPointer = (clientY) => {
      const delta = clientY - startY;
      const nextTopHeight = Math.min(
        Math.max(initialTopHeight + delta, 120),
        shellHeight - 120,
      );
      setTopFlex(nextTopHeight / shellHeight);
      setChatPaneState("default");
    };

    const stopMouseDrag = () => {
      window.removeEventListener("mousemove", window[DRAG_START_MOUSE_KEY]);
      window.removeEventListener("mouseup", stopMouseDrag);
      delete window[DRAG_START_MOUSE_KEY];
    };

    const stopTouchDrag = () => {
      window.removeEventListener("touchmove", window[DRAG_START_TOUCH_KEY]);
      window.removeEventListener("touchend", stopTouchDrag);
      delete window[DRAG_START_TOUCH_KEY];
    };

    window[DRAG_START_MOUSE_KEY] = (event) => updateFromPointer(event.clientY);
    window[DRAG_START_TOUCH_KEY] = (event) => {
      const touch = event.touches[0];
      if (touch) updateFromPointer(touch.clientY);
    };

    window.addEventListener("mousemove", window[DRAG_START_MOUSE_KEY]);
    window.addEventListener("mouseup", stopMouseDrag);
    window.addEventListener("touchmove", window[DRAG_START_TOUCH_KEY], { passive: true });
    window.addEventListener("touchend", stopTouchDrag, { passive: true });
  }, [topFlex]);

  const topPaneStyle =
    chatPaneState === "maximized"
      ? { flex: "0 0 0%", minHeight: 0 }
      : isChatClosed
        ? { flex: "1 1 0%", minHeight: 0 }
        : { flex: `${topFlex} 1 0%` };
  const chatPaneStyle =
    chatPaneState === "minimized"
      ? { flex: "0 0 54px" }
      : chatPaneState === "maximized"
        ? { flex: "1 1 0%" }
        : { flex: `${1 - topFlex} 1 0%` };

  return (
    <section className={`${pageStyles.page} ${styles.wrapper}`} data-page="integrations">
      <div ref={splitRef} className={styles.splitShell}>
        <div
          className={`${styles.panelPane} ${
            chatPaneState === "maximized" ? styles.panelPaneHidden : ""
          }`}
          style={topPaneStyle}
        >
          {isOnboarding && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "0.75rem 1.25rem",
                borderBottom: "1px solid var(--bd)",
                background: "var(--bg-sb)",
                flexShrink: 0,
              }}
            >
              <div>
                <p style={{ margin: 0, fontWeight: 600, fontSize: 14, color: "var(--tp)" }}>
                  Connect your data sources
                </p>
                <p style={{ margin: 0, fontSize: 12, color: "var(--ts2)" }}>
                  Connect at least one to get started, or skip and connect later
                </p>
              </div>
              <button
                type="button"
                onClick={handleSkip}
                disabled={skipping}
                style={{
                  background: "none",
                  border: "1px solid var(--bdh)",
                  borderRadius: 999,
                  padding: "0.4rem 1rem",
                  fontSize: 13,
                  fontWeight: 600,
                  color: "var(--ts2)",
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                  flexShrink: 0,
                  marginLeft: "1rem",
                }}
              >
                {skipping ? "Saving…" : "Skip for now"}
              </button>
            </div>
          )}
          <div className={styles.panelBody}>
            <IntegrationsPanel />
          </div>
        </div>
        {!isChatClosed ? <div className={styles.paneDivider} aria-hidden="true">
          <button
            type="button"
            className={styles.dragHandle}
            onMouseDown={(event) => startResize(event.clientY)}
            onTouchStart={(event) => {
              const touch = event.touches[0];
              if (touch) startResize(touch.clientY);
            }}
            aria-label="Resize integrations chat"
          />
          <div className={styles.dividerControls}>
            <div className={styles.chatTitleWrap}>
              <span className={styles.chatTitle}>Zeqi chat</span>
              <div className={styles.chatContextTrail}>
                {visibleChatContextTrail.map((context) => {
                  const isDismissible = context.key !== "main";
                  return (
                    <span key={context.key} className={styles.chatContextPill}>
                      <span className={styles.chatContextPillText}>{context.label}</span>
                      {isDismissible ? (
                        <button
                          type="button"
                          className={styles.chatContextPillReset}
                          onClick={() => resetSpecificContext(context.key, context.label)}
                          aria-label={`Clear ${context.label} context`}
                        >
                          <X size={12} strokeWidth={2} />
                        </button>
                      ) : null}
                    </span>
                  );
                })}
              </div>
            </div>
            <div className={styles.controlButtons}>
              <button
                type="button"
                className={styles.controlButton}
                onClick={handleExpandChat}
                aria-label="Expand integrations chat"
              >
                <ArrowUp size={16} strokeWidth={1.75} />
              </button>
              <button
                type="button"
                className={styles.controlButton}
                onClick={handleExpandReports}
                aria-label="Restore integrations chat"
              >
                <ArrowDown size={16} strokeWidth={1.75} />
              </button>
              <button
                type="button"
                className={styles.controlButton}
                onClick={() => setChatPaneState("minimized")}
                aria-label="Close integrations chat"
              >
                <X size={16} strokeWidth={1.75} />
              </button>
            </div>
          </div>
        </div> : null}
        {!isChatClosed ? <div
          className={`${styles.chatPane} ${
            chatPaneState === "minimized"
              ? styles.chatPaneMinimized
              : chatPaneState === "maximized"
                ? styles.chatPaneMaximized
                : ""
          }`}
          style={chatPaneStyle}
          >
            <div className={styles.chatBody}>
            <ChatPage
              inline
              variant="inline"
              channelId="integrations"
              chatContext={activeChatContext}
              inputPlaceholder={`Ask about ${activeChatContext.label}...`}
            />
            </div>
        </div> : null}
      </div>
      {isChatClosed ? (
        <div className={styles.chatReopenDock}>
          <button
            type="button"
            className={styles.chatReopenField}
            onClick={() => setChatPaneState("default")}
            aria-label="Reopen Zeqi chat"
            title="Reopen Zeqi chat"
          >
            <span className={styles.chatReopenFieldIcon}>
              <Plus size={18} strokeWidth={1.9} />
            </span>
            <span className={styles.chatReopenFieldText}>
              Ask about {activeChatContext.label}...
            </span>
            <span className={styles.chatReopenFieldSend}>
              <Send size={16} strokeWidth={1.9} />
            </span>
          </button>
        </div>
      ) : null}
    </section>
  );
};

export default IntegrationsPage;
